import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
  Typography, Box, TextField, Grid, Paper, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, CircularProgress, Alert, Divider,
  LinearProgress, Chip, FormControl, InputLabel, Select, MenuItem,
  styled, SelectChangeEvent, Tooltip, Collapse
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';
import PreviewIcon from '@mui/icons-material/Preview';
import SaveIcon from '@mui/icons-material/Save';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleIcon from '@mui/icons-material/People';
import UpdateIcon from '@mui/icons-material/Update';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import * as XLSX from 'xlsx';
import { useAuth, Branch } from '../../../../contexts/AuthContext';
import Autocomplete from '@mui/material/Autocomplete';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import phoneCallService, {
  BulkCreateCustomerItemDto,
  BulkCreateCustomerResultDto,
  CheckExistingPhonesResult
} from '../../../../services/phoneCallService';
import { accountingApi } from '../../../../config/apiClient';
import { ACCOUNTING_API } from '../../../../config/api';

interface AccountGroup {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
}

interface CustomerImportModalProps {
  open: boolean;
  onClose: () => void;
}

interface ColumnMapping {
  fieldName: string;
  label: string;
  selected: string;
  required: boolean;
}

interface PreviewRow {
  [key: string]: any;
}

interface ImportLog {
  time: string;
  type: 'info' | 'success' | 'warning' | 'error';
  message: string;
}

const ImportArea = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  cursor: 'pointer',
  '&:hover': { backgroundColor: theme.palette.action.hover },
}));

const FIELD_DEFINITIONS: { fieldName: string; label: string; required: boolean }[] = [
  { fieldName: 'customerName', label: 'Müşteri Adı (adisoyadi)', required: true },
  { fieldName: 'phone', label: 'Telefon 1 (tel1)', required: false },
  { fieldName: 'mobilePhone', label: 'Telefon 2 (tel2)', required: false },
  { fieldName: 'firmName', label: 'Firma Adı (firmaadi)', required: false },
  { fieldName: 'city', label: 'Şehir', required: false },
  { fieldName: 'district', label: 'İlçe / Semt (semt)', required: false },
  { fieldName: 'mahalle', label: 'Mahalle', required: false },
  { fieldName: 'cadde', label: 'Cadde', required: false },
  { fieldName: 'sokak', label: 'Sokak', required: false },
  { fieldName: 'apartman', label: 'Apartman', required: false },
  { fieldName: 'siteAdi', label: 'Site Adı (siteadi)', required: false },
  { fieldName: 'blok', label: 'Blok', required: false },
  { fieldName: 'binaNo', label: 'Bina No (binano)', required: false },
  { fieldName: 'kat', label: 'Kat', required: false },
  { fieldName: 'daireNo', label: 'Daire No (daire)', required: false },
  { fieldName: 'address', label: 'Açık Adres (adres)', required: false },
  { fieldName: 'deliveryZone', label: 'Bölge (bolge)', required: false },
];

const normalizePhone = (val: string): string => {
  if (!val) return '';
  return val.toString().replace(/[\s\-\(\)]/g, '').trim();
};

const normalizeTurkish = (s: string): string =>
  s.replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ö/g, 'o')
   .replace(/ü/g, 'u').replace(/ç/g, 'c').replace(/ğ/g, 'g')
   .replace(/İ/g, 'i').replace(/Ş/g, 's').replace(/Ö/g, 'o')
   .replace(/Ü/g, 'u').replace(/Ç/g, 'c').replace(/Ğ/g, 'g');

const nowStr = () => new Date().toLocaleTimeString('tr-TR');

const CustomerImportModal: React.FC<CustomerImportModalProps> = ({
  open, onClose
}) => {
  const { userDetails, branches } = useAuth();
  const { colors: themeColors } = useThemeContext();

  const [step, setStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState('');
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [startRow, setStartRow] = useState(2);
  const [excelColumns, setExcelColumns] = useState<string[]>([]);
  const [excelData, setExcelData] = useState<any[][]>([]);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<AccountGroup | null>(null);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [existingCheck, setExistingCheck] = useState<CheckExistingPhonesResult | null>(null);
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [savingData, setSavingData] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isCancelled, setIsCancelled] = useState(false);
  const [importResult, setImportResult] = useState<{
    totalCount: number; successCount: number; failedCount: number;
    newAccountCount: number; newAddressCount: number; updatedAccountCount: number;
    failedItems: Array<{ CustomerName: string; Phone?: string; Error: string }>;
  } | null>(null);

  const [logs, setLogs] = useState<ImportLog[]>([]);
  const [showLogs, setShowLogs] = useState(false);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const cancelledRef = useRef(false);

  const addLog = useCallback((type: ImportLog['type'], message: string) => {
    const entry: ImportLog = { time: nowStr(), type, message };
    setLogs(prev => [...prev, entry]);
    console.log(`[MüşteriAktarım][${type.toUpperCase()}] ${message}`);
  }, []);

  const previewStats = React.useMemo(() => {
    if (previewData.length === 0 || !existingCheck) return null;
    const existingPhoneSet = new Set(existingCheck.ExistingPhones || []);
    let withName = 0, withoutName = 0, existingWithName = 0, existingWithoutName = 0;
    previewData.forEach(row => {
      const hasName = !!(row.customerName && row.customerName.trim() && row.customerName.trim() !== '-');
      const p1 = normalizePhone(row.phone);
      const p2 = normalizePhone(row.mobilePhone);
      const isExisting = existingPhoneSet.has(p1) || existingPhoneSet.has(p2);
      if (hasName) { withName++; if (isExisting) existingWithName++; }
      else { withoutName++; if (isExisting) existingWithoutName++; }
    });
    return { withName, withoutName, existingWithName, existingWithoutName, existingTotal: existingCheck.ExistingCount };
  }, [previewData, existingCheck]);

  useEffect(() => {
    if (open) {
      loadAccountGroups();
    } else {
      setStep(1); setFile(null); setFileName(''); setSheetNames([]);
      setSelectedSheet(''); setExcelColumns([]); setExcelData([]);
      setColumnMappings([]); setPreviewData([]); setError(null);
      setSelectedBranch(null); setSelectedGroup(null);
      setExistingCheck(null); setCheckingExisting(false);
      setSavingData(false); setLoadingProgress(0); setIsCancelled(false);
      setImportResult(null); setLogs([]); setShowLogs(false);
      cancelledRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (showLogs && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, showLogs]);

  const loadAccountGroups = async () => {
    setGroupsLoading(true);
    try {
      const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_GROUP_BY_COMPANY);
      const result = response.data;
      const sqlGroups = result.data || result;
      const groupsList: AccountGroup[] = sqlGroups.map((data: any) => ({
        id: data.firebaseId || data.id.toString(),
        name: data.name || '',
        parentId: data.parentFirebaseId || null,
        level: data.level || 1,
      }));
      groupsList.sort((a, b) => a.name.localeCompare(b.name));
      setGroups(groupsList);
    } catch (err) {
      console.error('Cari gruplar yüklenemedi:', err);
    } finally {
      setGroupsLoading(false);
    }
  };

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    setFileName(selectedFile.name);
    setError(null);
    setLoading(true);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array', codepage: 1254 });
        const sheets = workbook.SheetNames;
        setSheetNames(sheets);
        addLog('info', `Dosya yüklendi: ${selectedFile.name} (${sheets.length} sayfa)`);
        if (sheets.length > 0) {
          setSelectedSheet(sheets[0]);
          parseSheet(workbook, sheets[0]);
        }
      } catch (err) {
        setError('Excel dosyası okunamadı. Lütfen geçerli bir .xls veya .xlsx dosyası yükleyin.');
        addLog('error', `Dosya okuma hatası: ${err}`);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  }, [addLog]);

  const parseSheet = useCallback((workbook: XLSX.WorkBook, sheetName: string) => {
    const ws = workbook.Sheets[sheetName];
    const rawData: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    if (rawData.length === 0) { setError('Seçilen sayfa boş'); return; }

    const headerRow = rawData[0] || [];
    const cols = headerRow.map((h: any, i: number) => h?.toString()?.trim() || `Sütun ${i + 1}`);
    setExcelColumns(cols);
    setExcelData(rawData);

    addLog('info', `Sayfa "${sheetName}": ${rawData.length - 1} satır, ${cols.length} sütun`);
    addLog('info', `Excel sütunları: ${cols.join(', ')}`);

    const mappings: ColumnMapping[] = FIELD_DEFINITIONS.map(fd => {
      let autoMatch = '';
      const lowerCols = cols.map((c: string) => normalizeTurkish(c.toLowerCase().replace(/[\s_\-]/g, '')));
      const fieldLower = fd.fieldName.toLowerCase();

      const directMap: Record<string, string[]> = {
        'customerName': ['adisoyadi', 'adsoyad', 'musteriad', 'musteriadı', 'musteri', 'ad', 'adsoyadi', 'adisoyad'],
        'phone': ['tel1', 'telefon1', 'telefon', 'tel'],
        'mobilePhone': ['tel2', 'telefon2', 'ceptel', 'ceptelefon', 'cep'],
        'firmName': ['firmaadi', 'firma', 'sirket', 'firmaad'],
        'city': ['sehir', 'il', 'city'],
        'district': ['semt', 'ilce', 'district'],
        'mahalle': ['mahalle', 'mah'],
        'cadde': ['cadde', 'cad'],
        'sokak': ['sokak', 'sok'],
        'apartman': ['apartman', 'apt', 'bina'],
        'siteAdi': ['siteadi', 'site', 'sitead'],
        'blok': ['blok', 'block'],
        'binaNo': ['binano', 'bina_no'],
        'kat': ['kat', 'floor'],
        'daireNo': ['daire', 'daireno', 'daire_no'],
        'address': ['adres', 'acikadres', 'address'],
        'deliveryZone': ['bolge', 'region'],
      };

      const aliases = directMap[fd.fieldName] || [fieldLower];
      for (let ci = 0; ci < lowerCols.length; ci++) {
        const normalizedCol = lowerCols[ci];
        if (aliases.includes(normalizedCol)) {
          autoMatch = cols[ci];
          break;
        }
        for (const alias of aliases) {
          if (normalizedCol.includes(alias) || alias.includes(normalizedCol)) {
            autoMatch = cols[ci];
            break;
          }
        }
        if (autoMatch) break;
      }

      if (autoMatch) {
        addLog('info', `Otomatik eşleşme: "${fd.label}" → "${autoMatch}"`);
      }

      return { fieldName: fd.fieldName, label: fd.label, required: fd.required, selected: autoMatch };
    });
    setColumnMappings(mappings);

    const sampleRow = rawData[1];
    if (sampleRow) {
      const nameMap = mappings.find(m => m.fieldName === 'customerName');
      if (nameMap?.selected) {
        const nameIdx = cols.indexOf(nameMap.selected);
        const sampleName = nameIdx >= 0 ? sampleRow[nameIdx] : '(bulunamadı)';
        addLog('info', `Örnek müşteri adı (satır 2): "${sampleName}" (index: ${nameIdx})`);
      }
    }
  }, [addLog]);

  const handleSheetChange = useCallback((e: SelectChangeEvent) => {
    const sheetName = e.target.value;
    setSelectedSheet(sheetName);
    if (file) {
      setLoading(true);
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const data = new Uint8Array(evt.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array', codepage: 1254 });
          parseSheet(workbook, sheetName);
        } finally { setLoading(false); }
      };
      reader.readAsArrayBuffer(file);
    }
  }, [file, parseSheet]);

  const handleMappingChange = useCallback((fieldName: string, excelCol: string) => {
    setColumnMappings(prev => prev.map(m =>
      m.fieldName === fieldName ? { ...m, selected: excelCol } : m
    ));
    addLog('info', `Manuel eşleşme: "${fieldName}" → "${excelCol || '(kaldırıldı)'}"`);
  }, [addLog]);

  const handlePreview = useCallback(async () => {
    setError(null);
    const nameMapping = columnMappings.find(m => m.fieldName === 'customerName');
    if (!nameMapping?.selected) {
      setError('Müşteri Adı sütunu eşleştirilmeli');
      return;
    }

    addLog('info', 'Önizleme oluşturuluyor...');

    const rows: PreviewRow[] = [];
    const colIndexMap: Record<string, number> = {};
    columnMappings.forEach(m => {
      if (m.selected) {
        const idx = excelColumns.indexOf(m.selected);
        if (idx >= 0) colIndexMap[m.fieldName] = idx;
      }
    });

    addLog('info', `Sütun index haritası: ${JSON.stringify(colIndexMap)}`);

    let emptyNameCount = 0;
    for (let i = startRow - 1; i < excelData.length; i++) {
      const row = excelData[i];
      if (!row || row.every((c: any) => !c && c !== 0)) continue;

      const item: PreviewRow = { _rowIndex: i + 1 };
      Object.entries(colIndexMap).forEach(([field, idx]) => {
        const rawVal = row[idx];
        item[field] = rawVal !== null && rawVal !== undefined ? rawVal.toString().trim() : '';
      });

      if (!item.customerName && !item.phone && !item.mobilePhone) continue;
      if (!item.customerName || item.customerName === '-') emptyNameCount++;
      rows.push(item);
    }

    if (emptyNameCount > 0) {
      addLog('warning', `${emptyNameCount} satırda müşteri adı boş veya "-"`);
    }
    addLog('info', `Önizleme: ${rows.length} geçerli satır (isimli: ${rows.length - emptyNameCount}, isimsiz: ${emptyNameCount})`);

    setPreviewData(rows);
    if (rows.length === 0) {
      setError('Önizleme için geçerli satır bulunamadı');
      addLog('error', 'Hiç geçerli satır bulunamadı');
      return;
    }

    setStep(2);

    setCheckingExisting(true);
    addLog('info', 'Mevcut müşteri kontrolü başlatılıyor...');
    try {
      const allPhones = rows
        .flatMap(r => [normalizePhone(r.phone), normalizePhone(r.mobilePhone)])
        .filter(p => p && p.length >= 7);
      const uniquePhones = Array.from(new Set(allPhones));
      addLog('info', `${uniquePhones.length} benzersiz telefon numarası kontrol edilecek`);
      if (uniquePhones.length > 0) {
        const result = await phoneCallService.checkExistingPhones(uniquePhones);
        setExistingCheck(result);
        addLog('info', `Mevcut müşteri kontrolü tamamlandı: ${result.ExistingCount} mevcut, ${result.NewCount} yeni`);
      }
    } catch (err) {
      addLog('warning', `Mevcut müşteri kontrolü başarısız: ${err}`);
    } finally {
      setCheckingExisting(false);
    }
  }, [columnMappings, excelColumns, excelData, startRow, addLog]);

  const handleImport = useCallback(async () => {
    if (!selectedGroup) {
      setError('Lütfen bir cari grup seçin');
      return;
    }

    setSavingData(true);
    setLoadingProgress(0);
    setIsCancelled(false);
    cancelledRef.current = false;
    setError(null);
    setStep(3);
    setShowLogs(true);

    addLog('info', `Aktarım başlatılıyor: ${previewData.length} satır, Şube: ${selectedBranch?.name || '-'} (id: ${selectedBranch?.id || '-'}), Grup: ${selectedGroup.name} (id: ${selectedGroup.id})`);
    addLog('info', `API: ${selectedGroup.id} GroupId ile gönderilecek`);

    const BATCH_SIZE = 100;
    const totalBatches = Math.ceil(previewData.length / BATCH_SIZE);
    let totalSuccess = 0, totalFailed = 0, totalNewAccounts = 0, totalNewAddresses = 0, totalUpdated = 0;
    const allFailedItems: Array<{ CustomerName: string; Phone?: string; Error: string }> = [];

    addLog('info', `${totalBatches} batch halinde gönderilecek (batch boyutu: ${BATCH_SIZE})`);
    const importStartTime = Date.now();

    for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
      if (cancelledRef.current) {
        addLog('warning', `Aktarım kullanıcı tarafından iptal edildi (batch ${batchIndex + 1}/${totalBatches} öncesi)`);
        addLog('info', `İptal anına kadar: Başarılı:${totalSuccess} Yeni:${totalNewAccounts} Adres:${totalNewAddresses} Güncellenen:${totalUpdated} Hata:${totalFailed}`);
        break;
      }

      const start = batchIndex * BATCH_SIZE;
      const end = Math.min(start + BATCH_SIZE, previewData.length);
      const batchRows = previewData.slice(start, end);

      const items: BulkCreateCustomerItemDto[] = batchRows.map(row => ({
        CustomerName: row.customerName || '-',
        Phone: normalizePhone(row.phone) || undefined,
        MobilePhone: normalizePhone(row.mobilePhone) || undefined,
        FirmName: row.firmName || undefined,
        Country: 'Türkiye',
        City: row.city || undefined,
        District: row.district || undefined,
        Address: row.address || undefined,
        DeliveryZone: row.deliveryZone || undefined,
        Mahalle: row.mahalle || undefined,
        Cadde: row.cadde || undefined,
        Sokak: row.sokak || undefined,
        Apartman: row.apartman || undefined,
        SiteAdi: row.siteAdi || undefined,
        Blok: row.blok || undefined,
        BinaNo: row.binaNo || undefined,
        Kat: row.kat || undefined,
        DaireNo: row.daireNo || undefined,
      }));

      try {
        const batchLabel = `Batch ${batchIndex + 1}/${totalBatches}`;
        const phoneSample = items.slice(0, 3).map(i => i.Phone || '?').join(', ');
        addLog('info', `${batchLabel} gönderiliyor... (${items.length} kayıt, ilk telefonlar: ${phoneSample})`);

        const result = await phoneCallService.bulkCreateCustomers({
          BranchId: selectedBranch?.id,
          GroupId: selectedGroup!.id,
          Items: items,
        });
        totalSuccess += result.SuccessCount;
        totalFailed += result.FailedCount;
        totalNewAccounts += result.NewAccountCount;
        totalNewAddresses += result.NewAddressCount;
        totalUpdated += result.UpdatedAccountCount || 0;
        if (result.FailedItems) allFailedItems.push(...result.FailedItems);

        addLog('success', `${batchLabel}: ✓${result.SuccessCount} yeni:${result.NewAccountCount} adres:${result.NewAddressCount} güncellenen:${result.UpdatedAccountCount || 0} hata:${result.FailedCount}`);

        if (result.FailedItems && result.FailedItems.length > 0) {
          result.FailedItems.forEach((fi: any) => {
            addLog('warning', `  → Hatalı kayıt: ${fi.CustomerName || '?'} (${fi.Phone || '?'}) - ${fi.Error}`);
          });
        }
      } catch (err: any) {
        const status = err?.response?.status || '?';
        const errData = err?.response?.data;
        const errMsg = errData?.message || errData?.title || err?.message || 'Bilinmeyen hata';
        const errDetail = errData?.error || '';
        const batchLabel = `Batch ${batchIndex + 1}/${totalBatches}`;

        addLog('error', `${batchLabel} HATA [HTTP ${status}]: ${errMsg}`);
        if (errDetail) {
          addLog('error', `${batchLabel} Detay: ${errDetail}`);
        }
        addLog('error', `${batchLabel} Batch aralığı: satır ${start + 1}-${end}, ilk telefon: ${items[0]?.Phone || '?'}, son telefon: ${items[items.length - 1]?.Phone || '?'}`);

        console.error(`[BulkImport] ${batchLabel} tam hata:`, err?.response?.data || err);

        totalFailed += items.length;
        items.forEach(item => {
          allFailedItems.push({
            CustomerName: item.CustomerName,
            Phone: item.Phone,
            Error: `[HTTP ${status}] ${errMsg}`
          });
        });
      }

      const progress = ((batchIndex + 1) / totalBatches) * 100;
      setLoadingProgress(Math.min(100, progress));

      if ((batchIndex + 1) % 25 === 0 || batchIndex + 1 === totalBatches) {
        const elapsedSec = ((Date.now() - importStartTime) / 1000).toFixed(0);
        const avgPerBatch = ((Date.now() - importStartTime) / (batchIndex + 1) / 1000).toFixed(1);
        const remaining = ((totalBatches - batchIndex - 1) * parseFloat(avgPerBatch)).toFixed(0);
        addLog('info', `── Ara özet (${batchIndex + 1}/${totalBatches}): Başarılı:${totalSuccess} Yeni:${totalNewAccounts} Adres:${totalNewAddresses} Güncellenen:${totalUpdated} Hata:${totalFailed} | Geçen:${elapsedSec}s Kalan:~${remaining}s ──`);
      }

      if (cancelledRef.current) {
        addLog('warning', `Aktarım kullanıcı tarafından iptal edildi (batch ${batchIndex + 1}/${totalBatches} sonrası)`);
        addLog('info', `İptal anına kadar: Başarılı:${totalSuccess} Yeni:${totalNewAccounts} Adres:${totalNewAddresses} Güncellenen:${totalUpdated} Hata:${totalFailed}`);
        break;
      }
    }

    const wasCancelled = cancelledRef.current;
    const elapsed = ((Date.now() - importStartTime) / 1000).toFixed(1);
    addLog('info', `━━━━━━━━━━ ${wasCancelled ? 'İPTAL EDİLDİ' : 'AKTARIM SONUCU'} ━━━━━━━━━━`);
    addLog('info', `Süre: ${elapsed} saniye`);
    addLog('info', `Toplam satır: ${previewData.length}`);
    addLog('info', `Başarılı: ${totalSuccess}`);
    addLog('info', `Yeni müşteri: ${totalNewAccounts}`);
    addLog('info', `Yeni adres: ${totalNewAddresses}`);
    addLog('info', `Güncellenen müşteri: ${totalUpdated}`);
    addLog(totalFailed > 0 ? 'error' : 'success', `Hata: ${totalFailed}`);
    if (wasCancelled) {
      const remaining = previewData.length - totalSuccess - totalFailed;
      addLog('warning', `İşlenmemiş satır: ${remaining}`);
    }
    addLog('info', `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);

    setImportResult({
      totalCount: previewData.length,
      successCount: totalSuccess,
      failedCount: totalFailed,
      newAccountCount: totalNewAccounts,
      newAddressCount: totalNewAddresses,
      updatedAccountCount: totalUpdated,
      failedItems: allFailedItems,
    });
    setSavingData(false);
  }, [selectedGroup, selectedBranch, previewData, addLog]);

  const phoneStats = React.useMemo(() => {
    if (previewData.length === 0) return { total: 0, withPhone: 0, noPhone: 0, duplicatePhones: 0, emptyNames: 0 };
    const phones = new Set<string>();
    let withPhone = 0, noPhone = 0, dupes = 0, emptyNames = 0;
    previewData.forEach(row => {
      const p = normalizePhone(row.phone);
      if (p && p.length >= 7) {
        withPhone++;
        if (phones.has(p)) dupes++;
        phones.add(p);
      } else { noPhone++; }
      if (!row.customerName || row.customerName.trim() === '' || row.customerName.trim() === '-') emptyNames++;
    });
    return { total: previewData.length, withPhone, noPhone, duplicatePhones: dupes, emptyNames };
  }, [previewData]);

  const handleCancel = useCallback(() => {
    cancelledRef.current = true;
    setIsCancelled(true);
    addLog('warning', 'Kullanıcı aktarımı iptal etti');
  }, [addLog]);

  return (
    <Dialog open={open} onClose={(savingData && !isCancelled) ? undefined : onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 2, minHeight: '70vh' } }}>
      <DialogTitle sx={{ bgcolor: themeColors.primary, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleIcon />
          <Typography variant="h6">Müşteri Aktarımı (Excel)</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={`Adım ${step}/3`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
          <IconButton onClick={savingData ? handleCancel : onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* STEP 1: FILE UPLOAD & MAPPING */}
        {step === 1 && (
          <Box>
            {!file ? (
              <ImportArea component="label">
                <input type="file" hidden accept=".xls,.xlsx,.csv" onChange={handleFileUpload} />
                <UploadIcon sx={{ fontSize: 48, color: themeColors.primary, mb: 1 }} />
                <Typography variant="h6">Excel Dosyası Yükle</Typography>
                <Typography variant="body2" color="text.secondary">.xls, .xlsx veya .csv dosyası seçin</Typography>
              </ImportArea>
            ) : (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <strong>{fileName}</strong> yüklendi ({excelData.length - 1} satır)
                </Alert>

                {sheetNames.length > 1 && (
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Sayfa</InputLabel>
                    <Select value={selectedSheet} onChange={handleSheetChange} label="Sayfa">
                      {sheetNames.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      options={branches}
                      getOptionLabel={(option) => option.name}
                      value={selectedBranch}
                      onChange={(_, value) => setSelectedBranch(value)}
                      renderInput={(params) => (
                        <TextField {...params} label="Şube Seçimi *" placeholder="Şube seçin" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      loading={groupsLoading}
                      options={groups}
                      getOptionLabel={(option) => option.name}
                      value={selectedGroup}
                      onChange={(_, value) => setSelectedGroup(value)}
                      renderInput={(params) => (
                        <TextField {...params} label="Cari Grup Seçimi *" placeholder="Cari grup seçin" />
                      )}
                    />
                  </Grid>
                </Grid>

                <TextField fullWidth size="small" type="number" label="Başlangıç Satırı (başlık hariç)" value={startRow}
                  onChange={(e) => setStartRow(Math.max(1, parseInt(e.target.value) || 1))} sx={{ mb: 3 }} />

                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  Sütun Eşleştirme
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={1.5}>
                  {columnMappings.map(mapping => (
                    <Grid item xs={12} md={6} key={mapping.fieldName}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{mapping.label}{mapping.required ? ' *' : ''}</InputLabel>
                        <Select value={mapping.selected}
                          onChange={(e) => handleMappingChange(mapping.fieldName, e.target.value)}
                          label={mapping.label}>
                          <MenuItem value="">(Eşleştirme yok)</MenuItem>
                          {excelColumns.map(col => <MenuItem key={col} value={col}>{col}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        )}

        {/* STEP 2: PREVIEW */}
        {step === 2 && (
          <Box>
            {checkingExisting && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Sistemdeki mevcut müşteriler kontrol ediliyor...</Typography>
                </Box>
              </Alert>
            )}

            {existingCheck && !checkingExisting && (
              <Alert
                severity={existingCheck.ExistingCount > 0 ? 'info' : 'success'}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                  {existingCheck.ExistingCount > 0
                    ? `${existingCheck.ExistingCount} telefon numarası sistemde zaten kayıtlı`
                    : 'Tüm telefon numaraları yeni - sistemde mevcut kayıt bulunamadı'}
                </Typography>
                {existingCheck.ExistingCount > 0 && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Mevcut müşterilerin <strong>eksik bilgileri (ad, telefon 2, firma adı)</strong> Excel'deki verilerle güncellenecek.
                      Farklı adresleri varsa yeni adres olarak eklenecek.
                    </Typography>
                    <Typography variant="body2">
                      Yeni müşteri olarak eklenecek: <strong>{existingCheck.NewCount}</strong>
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}

            {previewStats && previewStats.existingTotal > 0 && (
              <Alert severity="info" icon={<UpdateIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                  İşlem Özeti
                </Typography>
                <Typography variant="body2" component="div">
                  <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                    <li><strong>{existingCheck?.ExistingCount || 0}</strong> telefon numarası sistemde zaten kayıtlı — bu müşteriler tekrar oluşturulmayacak.</li>
                    <li>Sistemde kayıtlı müşterilerden sadece <strong>eksik alanları olanlar</strong> (ad, firma adı, telefon 2 boşsa) Excel'den güncellenecek. Bilgisi tam olanlar atlanacak.</li>
                    {existingCheck && existingCheck.NewCount > 0 && (
                      <li><strong>{existingCheck.NewCount}</strong> yeni müşteri oluşturulacak.</li>
                    )}
                    <li>Mevcut müşterilerin farklı adresleri varsa yeni adres olarak eklenecek, aynı adresler atlanacak.</li>
                    {previewStats.withoutName > 0 && (
                      <li><strong>{previewStats.withoutName}</strong> satırda müşteri adı boş — bu satırlarda isim güncellemesi yapılamaz.</li>
                    )}
                  </ul>
                </Typography>
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Chip label={`Şube: ${selectedBranch?.name || '-'}`} color="primary" variant="outlined" size="small" />
              <Chip label={`Grup: ${selectedGroup?.name || '-'}`} color="secondary" variant="outlined" size="small" />
              <Chip label={`Toplam: ${phoneStats.total}`} color="default" size="small" />
              <Chip label={`Telefonlu: ${phoneStats.withPhone}`} color="success" size="small" />
              {phoneStats.noPhone > 0 && <Chip label={`Telefonsuz: ${phoneStats.noPhone}`} color="warning" size="small" />}
              {phoneStats.duplicatePhones > 0 && <Chip label={`Tekrarlı Tel: ${phoneStats.duplicatePhones}`} color="info" size="small" />}
              {phoneStats.emptyNames > 0 && (
                <Chip label={`İsimsiz: ${phoneStats.emptyNames}`} color="error" variant="outlined" size="small" />
              )}
              {existingCheck && (
                <>
                  <Chip label={`Mevcut: ${existingCheck.ExistingCount}`} color="warning" variant="filled" size="small" />
                  <Chip label={`Yeni: ${existingCheck.NewCount}`} color="success" variant="filled" size="small" />
                </>
              )}
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Durum</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Müşteri Adı</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Telefon</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Telefon 2</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Mahalle</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Cadde/Sokak</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Bina/Kat/Daire</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.slice(0, 100).map((row, i) => {
                    const p1 = normalizePhone(row.phone);
                    const p2 = normalizePhone(row.mobilePhone);
                    const existingPhoneSet = new Set(existingCheck?.ExistingPhones || []);
                    const isExisting = existingPhoneSet.has(p1) || existingPhoneSet.has(p2);
                    const hasName = !!(row.customerName && row.customerName.trim() && row.customerName.trim() !== '-');
                    return (
                      <TableRow key={i} hover sx={isExisting ? { bgcolor: 'action.hover' } : undefined}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>
                          {isExisting ? (
                            <Tooltip title="Mevcut müşteri - eksik bilgiler güncellenecek">
                              <Chip label="Güncelle" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
                            </Tooltip>
                          ) : (
                            <Chip label="Yeni" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
                          )}
                        </TableCell>
                        <TableCell sx={{
                          maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          color: hasName ? 'inherit' : 'error.main',
                          fontStyle: hasName ? 'normal' : 'italic'
                        }}>
                          {hasName ? row.customerName : '(boş)'}
                        </TableCell>
                        <TableCell>{row.phone}</TableCell>
                        <TableCell>{row.mobilePhone}</TableCell>
                        <TableCell>{row.mahalle}</TableCell>
                        <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[row.cadde, row.sokak].filter(Boolean).join(', ')}</TableCell>
                        <TableCell>{[row.binaNo && `No:${row.binaNo}`, row.kat && `K:${row.kat}`, row.daireNo && `D:${row.daireNo}`].filter(Boolean).join(' ')}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {previewData.length > 100 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                İlk 100 satır gösteriliyor (toplam {previewData.length})
              </Typography>
            )}
          </Box>
        )}

        {/* STEP 3: IMPORT PROGRESS & RESULT */}
        {step === 3 && (
          <Box>
            {savingData && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                {!isCancelled ? (
                  <>
                    <CircularProgress size={60} sx={{ mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>Müşteriler Aktarılıyor...</Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" sx={{ mb: 1, color: 'warning.main' }}>İptal ediliyor... Mevcut batch tamamlanıyor.</Typography>
                  </>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {Math.round(loadingProgress)}% tamamlandı
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={loadingProgress}
                  color={isCancelled ? 'warning' : 'primary'}
                  sx={{ height: 10, borderRadius: 5 }}
                />
                {!isCancelled && (
                  <Button
                    onClick={handleCancel}
                    variant="contained"
                    color="error"
                    size="large"
                    sx={{ mt: 3, px: 5, fontWeight: 'bold' }}
                  >
                    Aktarımı İptal Et
                  </Button>
                )}
                {isCancelled && (
                  <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
                    Mevcut batch tamamlandıktan sonra işlem duracak...
                  </Typography>
                )}
              </Box>
            )}

            {!savingData && importResult && (
              <Box>
                <Alert severity={importResult.failedCount === 0 ? 'success' : 'warning'} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {importResult.failedCount === 0 ? 'Aktarım Başarıyla Tamamlandı!' : 'Aktarım Tamamlandı (Bazı Hatalar Var)'}
                  </Typography>
                </Alert>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={2.4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">{importResult.totalCount}</Typography>
                      <Typography variant="body2">Toplam Satır</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">{importResult.successCount}</Typography>
                      <Typography variant="body2">Başarılı</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="info.main">{importResult.newAccountCount}</Typography>
                      <Typography variant="body2">Yeni Müşteri</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="secondary.main">{importResult.newAddressCount}</Typography>
                      <Typography variant="body2">Yeni Adres</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="warning.main">{importResult.updatedAccountCount}</Typography>
                      <Typography variant="body2">Güncellenen</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {importResult.failedCount > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                      <ErrorOutlineIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      {importResult.failedCount} satır aktarılamadı:
                    </Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Müşteri</TableCell>
                            <TableCell>Telefon</TableCell>
                            <TableCell>Hata</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {importResult.failedItems.slice(0, 50).map((item, i) => (
                            <TableRow key={i}>
                              <TableCell>{item.CustomerName}</TableCell>
                              <TableCell>{item.Phone}</TableCell>
                              <TableCell sx={{ color: 'error.main' }}>{item.Error}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            )}

            {/* LOG PANEL - always visible during step 3 */}
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Button
                  size="small"
                  onClick={() => setShowLogs(!showLogs)}
                  endIcon={showLogs ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ textTransform: 'none', fontWeight: 'bold' }}
                >
                  Detaylı İşlem Logları ({logs.length})
                </Button>
                {savingData && (
                  <Chip label="Canlı" size="small" color="success" variant="outlined"
                    sx={{ animation: 'pulse 1.5s infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }} />
                )}
              </Box>
              <Collapse in={showLogs}>
                <Paper sx={{ maxHeight: 400, overflow: 'auto', p: 1.5, bgcolor: '#0d1117', borderRadius: 1, border: '1px solid #30363d' }}>
                  {logs.map((log, i) => (
                    <Typography key={i} variant="caption" component="div" sx={{
                      fontFamily: '"Cascadia Code", "Fira Code", "Consolas", monospace',
                      py: 0.15, fontSize: '0.72rem', lineHeight: 1.6,
                      color: log.type === 'error' ? '#f85149' :
                             log.type === 'warning' ? '#d29922' :
                             log.type === 'success' ? '#3fb950' : '#8b949e',
                      ...(log.message.includes('━━━') ? { color: '#58a6ff', fontWeight: 'bold' } : {}),
                      ...(log.message.startsWith('  →') ? { pl: 2 } : {}),
                    }}>
                      <span style={{ color: '#484f58' }}>[{log.time}]</span>{' '}
                      <span style={{ color: log.type === 'error' ? '#f85149' : log.type === 'warning' ? '#d29922' : log.type === 'success' ? '#3fb950' : '#58a6ff', fontWeight: 'bold' }}>
                        {log.type === 'error' ? '✗' : log.type === 'warning' ? '⚠' : log.type === 'success' ? '✓' : 'ℹ'}
                      </span>{' '}
                      {log.message}
                    </Typography>
                  ))}
                  <div ref={logsEndRef} />
                </Paper>
              </Collapse>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 1 && (
          <>
            <Button onClick={onClose} color="inherit">İptal</Button>
            <Button variant="contained" startIcon={<PreviewIcon />} onClick={handlePreview}
              disabled={!file || loading || !selectedBranch || !selectedGroup || !columnMappings.find(m => m.fieldName === 'customerName')?.selected}
              sx={{ bgcolor: themeColors.primary, '&:hover': { bgcolor: themeColors.primary, filter: 'brightness(0.9)' } }}>
              Önizle
            </Button>
          </>
        )}
        {step === 2 && (
          <>
            <Button onClick={() => setStep(1)} color="inherit">Geri</Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleImport}
              disabled={previewData.length === 0}
              sx={{ bgcolor: themeColors.primary, '&:hover': { bgcolor: themeColors.primary, filter: 'brightness(0.9)' } }}>
              {existingCheck && existingCheck.NewCount > 0
                ? `${previewData.length} Satırı İşle (${existingCheck.NewCount} Yeni Müşteri)`
                : `${previewData.length} Satırı İşle`}
            </Button>
          </>
        )}
        {step === 3 && savingData && !isCancelled && (
          <Button variant="contained" color="error" onClick={handleCancel}
            sx={{ fontWeight: 'bold' }}>
            Aktarımı Durdur
          </Button>
        )}
        {step === 3 && !savingData && (
          <Button variant="contained" onClick={onClose} startIcon={<CheckCircleIcon />}
            sx={{ bgcolor: themeColors.primary, '&:hover': { bgcolor: themeColors.primary, filter: 'brightness(0.9)' } }}>
            Kapat
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CustomerImportModal;
